import React, { useState, useEffect } from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectsDashboardService from '../../services/projectsDashboardService'
import { formatCurrency } from '../../utils/currencyFormat'
import { 
  Users,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Brain,
  DollarSign,
  Target,
  AlertTriangle,
  CheckCircle,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  MessageSquare,
  FileText,
  Briefcase,
  FolderOpen,
  CheckSquare,
  Timer,
  Award,
  Circle,
  Settings,
  Layers,
  CreditCard,
  PieChart,
  UserCheck,
  Code,
  Wrench,
  Star,
  TrendingDown as TrendingDownIcon,
  DollarSign as DollarSignIcon,
  Clock as ClockIcon,
  BarChart3 as BarChartIcon,
  PieChart as PieChartIcon,
  Activity as ActivityIcon,
  Award as AwardIcon,
  HelpCircle
} from 'lucide-react'
import HelpModal from '../../components/HelpModal/HelpModal'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
// Consolidated CSS is imported globally'

const Projects = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()
  const [showHelp, setShowHelp] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const formatCurrencyDisplay = (amount, currency = 'INR') => formatCurrency(amount, currency, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const formatHoursTooltip = (hours) => `${Math.round((Number(hours) || 0) * 10) / 10} hrs`

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await projectsDashboardService.getDashboardStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading projects dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now'
    const date = new Date(timestamp)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
    return date.toLocaleDateString()
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Projects</h1>
          <p style={{textAlign: 'left', margin: 0}}>Track project progress and manage team collaboration</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/projects')} />

          {/* Key Metrics Grid */}
          <div className="dashboard-metrics-grid">
            {/* Project Metrics */}
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <FolderOpen size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.activeProjects?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.activeProjects?.change >= 0 ? '+' : ''}{dashboardData?.activeProjects?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.activeProjects?.count || 0)}
              </div>
              <div className="metric-title">Active Projects</div>
              <div className="metric-subtitle">vs last month</div>
            </div>

            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon">
                  <CheckSquare size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.tasksCompleted?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.tasksCompleted?.change >= 0 ? '+' : ''}{dashboardData?.tasksCompleted?.change || 0}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : (dashboardData?.tasksCompleted?.count?.toLocaleString() || '0')}
              </div>
              <div className="metric-title">Tasks Completed</div>
              <div className="metric-subtitle">This month</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Timer size={16} />
                </div>
                <div className={`metric-trend ${dashboardData?.onTimeDelivery?.changeType || 'positive'}`}>
                  <TrendingUp size={12} />
                  <span>{dashboardData?.onTimeDelivery?.change >= 0 ? '+' : ''}{dashboardData?.onTimeDelivery?.change?.toFixed(1) || 0}%</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.onTimeDelivery?.rate || 0}%`}
              </div>
              <div className="metric-title">On-Time Delivery</div>
              <div className="metric-subtitle">Industry avg: 78%</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <Award size={16} />
                </div>
                <div className="metric-trend neutral">
                  <CheckCircle size={12} />
                  <span>{dashboardData?.projectSuccessRate?.status || 'EXCELLENT'}</span>
                </div>
              </div>
              <div className="metric-value">
                {loading ? '...' : `${dashboardData?.projectSuccessRate?.rate || 0}%`}
              </div>
              <div className="metric-title">Project Success Rate</div>
              <div className="metric-subtitle">Client satisfaction</div>
            </div>
          </div>

          {/* Action Items & Alerts */}
          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Project Action Items</h3>
                <span className="item-count">{dashboardData?.actionItems?.length || 0} items</span>
              </div>
              <div className="action-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading action items...</div>
                ) : dashboardData?.actionItems && dashboardData.actionItems.length > 0 ? (
                  dashboardData.actionItems.map((item, index) => {
                    const iconMap = {
                      milestone: AlertTriangle,
                      resource: Users,
                      planning: Calendar
                    }
                    const Icon = iconMap[item.type] || AlertTriangle
                    return (
                      <div key={index} className={`action-item ${item.priority}`}>
                        <div className="action-icon">
                          <Icon size={16} />
                        </div>
                        <div className="action-content">
                          <div className="action-title">{item.title}</div>
                          <div className="action-description">{item.description}</div>
                        </div>
                        <div className="action-time">{formatTimeAgo(item.timestamp)}</div>
                      </div>
                    )
                  })
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No action items</div>
                )}
              </div>
            </div>

            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Project Team Activity</h3>
                <span className="activity-count">{dashboardData?.teamActivity?.length || 0} updates</span>
              </div>
              <div className="activity-list">
                {loading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading team activity...</div>
                ) : dashboardData?.teamActivity && dashboardData.teamActivity.length > 0 ? (
                  dashboardData.teamActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-avatar">{activity.employeeInitials || 'UN'}</div>
                      <div className="activity-content">
                        <div className="activity-text">
                          <strong>{activity.employeeName || 'Unknown'}</strong> {activity.activity}
                        </div>
                        <div className="activity-time">{formatTimeAgo(activity.timestamp)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>No recent activity</div>
                )}
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Project Progress</h3>
                <p>{dashboardData?.financialYear ? `FY ${dashboardData.financialYear}` : `FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart
                  series={dashboardData?.projectProgressTrend}
                  valueKey="revenue"
                  formatValue={(v) => `${Math.round((Number(v) || 0) * 10) / 10}h`}
                  formatTooltip={formatHoursTooltip}
                  emptyMessage="No hours logged this financial year"
                />
              </div>
            </div>

            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Project Status Distribution</h3>
                <p>Current portfolio</p>
              </div>
              <div className="chart-content">
                <div className="health-distribution">
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>Loading...</div>
                  ) : (dashboardData?.statusDistribution || []).filter((r) => (r.count || 0) > 0).length > 0 ? (
                    (dashboardData.statusDistribution || [])
                      .filter((r) => (r.count || 0) > 0)
                      .map((row, index) => {
                        const classes = ['excellent', 'good', 'at-risk', 'churned']
                        const cls = classes[index % classes.length]
                        const total = (dashboardData.statusDistribution || []).reduce((s, r) => s + (r.count || 0), 0)
                        const pct = row.percent != null
                          ? row.percent
                          : (total > 0 ? ((row.count || 0) / total) * 100 : 0)
                        return (
                          <div key={`${row.name}-${index}`} className={`health-segment ${cls}`}>
                            <div className="segment-bar" style={{ width: `${Math.max(pct, 4)}%` }} />
                            <div className="segment-info">
                              <span className="segment-label">{row.name}</span>
                              <span className="segment-count">{row.count ?? 0}</span>
                            </div>
                          </div>
                        )
                      })
                  ) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                      No projects in portfolio
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="projects-masters-content">
            <div className="projects-masters-header">
              <h2>Project Masters</h2>
              <p>Manage your project master data and configurations</p>
            </div>
            <div className="projects-masters-grid">
                {/* Project Type Master */}
                <div className="projects-master-card" onClick={() => navigate('/projects/project-type-master')} style={{ cursor: 'pointer' }}>
                  <div className="projects-master-icon">
                    <Layers size={24} />
                  </div>
                  <div className="projects-master-content">
                    <h3>Project Type Master</h3>
                    <p>Define project types: Internal, External, Support, Development</p>
                    <div className="projects-master-count">
                      {loading ? '…' : `${dashboardData?.masterData?.projectTypes ?? 0} Types`}
                    </div>
                  </div>
                </div>

                {/* Project Category Master */}
                <div className="projects-master-card" onClick={() => navigate('/projects/project-category-master')} style={{ cursor: 'pointer' }}>
                  <div className="projects-master-icon">
                    <FolderOpen size={24} />
                  </div>
                  <div className="projects-master-content">
                    <h3>Project Category Master</h3>
                    <p>Organize projects by categories with hierarchies and budgets</p>
                    <div className="projects-master-count">
                      {loading ? '…' : `${dashboardData?.masterData?.projectCategories ?? 0} Categories`}
                    </div>
                  </div>
                </div>


              {/* Pricing Model Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/pricing-model-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <CreditCard size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Pricing Model Master</h3>
                  <p>Pricing models: T&M, Fixed Price, Staff Augmentation</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.pricingModels ?? 0} Models`}
                  </div>
                </div>
              </div>

              {/* Project Status Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/project-status-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <Settings size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Project Status Master</h3>
                  <p>Project statuses: Planning, Active, On Hold, Completed</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.projectStatuses ?? 0} Statuses`}
                  </div>
                </div>
              </div>

              {/* Priority Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/priority-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <Star size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Priority Master</h3>
                  <p>Project priorities: Low, Medium, High, Critical</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.priorities ?? 0} Levels`}
                  </div>
                </div>
              </div>

              {/* Technology Stack Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/technology-stack-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <Code size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Technology Stack Master</h3>
                  <p>Technologies: React, Node.js, Python, Java, etc.</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.technologyStacks ?? 0} Technologies`}
                  </div>
                </div>
              </div>

              {/* Resource Role Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/resource-role-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <UserCheck size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Resource Role Master</h3>
                  <p>Roles: Developer, Designer, PM, QA, DevOps</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.resourceRoles ?? 0} Roles`}
                  </div>
                </div>
              </div>

              {/* Project Template Master */}
              <div className="projects-master-card" onClick={() => navigate('/projects/project-template-master')} style={{ cursor: 'pointer' }}>
                <div className="projects-master-icon">
                  <FileText size={24} />
                </div>
                <div className="projects-master-content">
                  <h3>Project Template Master</h3>
                  <p>Predefined project templates and workflows</p>
                  <div className="projects-master-count">
                    {loading ? '…' : `${dashboardData?.masterData?.projectTemplates ?? 0} Templates`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="projects-transactions-content">
            <div className="projects-transactions-header" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div>
                <h2>Project Management</h2>
                <p>Track and manage all project lifecycle activities</p>
              </div>
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
                <HelpCircle size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            </div>
            <div className="projects-transactions-grid">
              <ModuleOverviewCard
                prefix="projects-transaction"
                moduleName="Projects"
                showArrow={false}
                onClick={() => navigate('/projects?view=overview')}
              />
              {/* Project Management */}
              <div className="projects-transaction-card" onClick={() => navigate('/projects/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <FolderOpen size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Project List</h3>
                  <p>Create, view, and manage all projects</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.projects ?? 0} Projects`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/tasks/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <CheckSquare size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Task Management</h3>
                  <p>Project tasks, subtasks, and Kanban boards</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.tasks ?? 0} Tasks`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/timesheets/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <Timer size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Timesheets</h3>
                  <p>Time tracking, logging, and approval</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${(dashboardData?.transactionCounts?.hoursLogged ?? 0).toLocaleString('en-IN')} Hours`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card clickable" onClick={() => navigate('/projects/expenses/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <DollarSign size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Project Expenses</h3>
                  <p>Expense tracking and reimbursement</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : formatCurrencyDisplay(dashboardData?.transactionAmounts?.expenses ?? 0, 'INR')}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/milestones/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <Target size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Milestones</h3>
                  <p>Project milestones and deliverable tracking</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.milestones ?? 0} Milestones`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/resource-allocations/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <Users size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Resource Allocation</h3>
                  <p>Resource planning and team assignments</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.resources ?? 0} Resources`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/budget-plannings/list')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Budget Planning</h3>
                  <p>Project budgeting and cost tracking</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : formatCurrencyDisplay(dashboardData?.transactionAmounts?.budget ?? 0, 'INR')}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/billings')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <CreditCard size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Project Billing</h3>
                  <p>Billing events and revenue recognition</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : formatCurrencyDisplay(dashboardData?.transactionAmounts?.billings ?? 0, 'INR')}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/resource-calendar')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <Calendar size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Resource Calendar</h3>
                  <p>Resource scheduling and availability</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.resources ?? 0} Allocations`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/change-requests')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <Wrench size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Change Requests</h3>
                  <p>Project scope and requirement changes</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.changeRequests ?? 0} Requests`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/documents')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Project Documents</h3>
                  <p>Document management and version control</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.documents ?? 0} Documents`}
                  </div>
                </div>
              </div>

              <div className="projects-transaction-card" onClick={() => navigate('/projects/communications')} style={{ cursor: 'pointer' }}>
                <div className="projects-transaction-icon">
                  <MessageSquare size={24} />
                </div>
                <div className="projects-transaction-content">
                  <h3>Project Communication</h3>
                  <p>Team collaboration and client communication</p>
                  <div className="projects-transaction-count">
                    {loading ? '…' : `${dashboardData?.transactionCounts?.communications ?? 0} Messages`}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="projects-reports-content">
            <div className="projects-reports-header">
              <h2>Project Reports</h2>
              <p>Comprehensive project analytics and reporting</p>
            </div>
            <div className="projects-reports-grid">
              {/* Executive Reports */}
              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Project Dashboard</h3>
                  <p>Executive dashboard with key project metrics</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Project Performance</h3>
                  <p>Project delivery and performance analysis</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <Award size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>KPI Dashboard</h3>
                  <p>Key performance indicators and metrics</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              {/* Operational Reports */}
              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <Users size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Resource Utilization</h3>
                  <p>Team productivity and resource allocation reports</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <Timer size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Time Analysis</h3>
                  <p>Time tracking and billing analysis</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <Activity size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Project Activity</h3>
                  <p>Project activity logs and progress tracking</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              {/* Financial Reports */}
              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <DollarSign size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Budget Analysis</h3>
                  <p>Project budgeting and cost tracking reports</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Profitability Analysis</h3>
                  <p>Project profitability and ROI analysis</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <CreditCard size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Revenue Recognition</h3>
                  <p>Revenue tracking and billing reports</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              {/* Client Reports */}
              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <Star size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Client Satisfaction</h3>
                  <p>Client feedback and satisfaction reports</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <PieChart size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Project Portfolio</h3>
                  <p>Portfolio analysis and project distribution</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>

              <div className="projects-report-card">
                <div className="projects-report-icon">
                  <BarChartIcon size={24} />
                </div>
                <div className="projects-report-content">
                  <h3>Strategic Reports</h3>
                  <p>Strategic project planning and analysis</p>
                  <div className="projects-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Project Management – Help">
        {(
          <>
            <p>Project Management covers the full project lifecycle. Below is a detailed guide to each module.</p>

            <h3>1. Project List</h3>
            <p><strong>Purpose:</strong> Create, view, and manage all projects.</p>
            <p><strong>Details:</strong> From the list you can open existing projects or create a new one. The project form captures name, code, type, category, status, priority, dates, customer, budget, team, technology stack, milestones, and financial milestones. Use <strong>Project List</strong> to see all projects; use <strong>New Project</strong> to create.</p>

            <h3>2. Task Management</h3>
            <p><strong>Purpose:</strong> Project tasks, subtasks, and Kanban boards.</p>
            <p><strong>Details:</strong> Create and assign tasks to projects, with title, description, status, priority, assignee, due date, estimated hours, and parent task for subtasks. View and manage tasks in list or Kanban view.</p>

            <h3>3. Timesheets</h3>
            <p><strong>Purpose:</strong> Time tracking, logging, and approval.</p>
            <p><strong>Details:</strong> Log time against projects/tasks, submit timesheets for approval, and track hours. Includes time entry, weekly timesheet, overtime, and approval workflows. Access from <strong>Timesheets</strong> in the main menu or from the Projects Transactions card.</p>

            <h3>4. Project Expenses</h3>
            <p><strong>Purpose:</strong> Expense tracking and reimbursement.</p>
            <p><strong>Details:</strong> Record project-related expenses (category, amount, date, project, receipt), submit for approval, and track reimbursement. Create new expenses from the Project Expenses list.</p>

            <h3>5. Milestones</h3>
            <p><strong>Purpose:</strong> Project milestones and deliverable tracking.</p>
            <p><strong>Details:</strong> Define milestones with name, project, due date, status, description, and completion. Track deliverables and progress. Create from the Milestones list.</p>

            <h3>6. Resource Allocation</h3>
            <p><strong>Purpose:</strong> Resource planning and team assignments.</p>
            <p><strong>Details:</strong> Allocate resources (team members) to projects with role, start/end dates, allocation percentage, and notes. Manage who works on which project and when. Create from the Resource Allocation list.</p>

            <h3>7. Budget Planning</h3>
            <p><strong>Purpose:</strong> Project budgeting and cost tracking.</p>
            <p><strong>Details:</strong> Set and revise project budgets, track planned vs actual costs, and manage budget versions. Create budget plans linked to projects with amount, currency, and period. Create from the Budget Planning list.</p>

            <h3>8. Project Billing</h3>
            <p><strong>Purpose:</strong> Billing events and revenue recognition.</p>
            <p><strong>Details:</strong> Create billing events for projects (invoices, milestones, time-based), link to revenue recognition, and track billed amounts. Create from the Project Billing list.</p>

            <h3>9. Resource Calendar</h3>
            <p><strong>Purpose:</strong> Resource scheduling and availability.</p>
            <p><strong>Details:</strong> View and plan resource availability and scheduling across projects. Helps avoid over-allocation and plan capacity. (Availability may vary by deployment.)</p>

            <h3>10. Change Requests</h3>
            <p><strong>Purpose:</strong> Project scope and requirement changes.</p>
            <p><strong>Details:</strong> Log and approve change requests for scope, requirements, or timeline. Capture description, impact, status, and approval. Create from the Change Requests list.</p>

            <h3>11. Project Documents</h3>
            <p><strong>Purpose:</strong> Document management and version control.</p>
            <p><strong>Details:</strong> Attach and manage documents per project with version control. Store specs, contracts, and deliverables. (Availability may vary by deployment.)</p>

            <h3>12. Project Communication</h3>
            <p><strong>Purpose:</strong> Team collaboration and client communication.</p>
            <p><strong>Details:</strong> Central place for project-related messages, updates, and client communication. (Availability may vary by deployment.)</p>

            <h3>How to use</h3>
            <p>Click a card to open that module (list or main screen). Use <strong>New</strong> or <strong>Create</strong> buttons within each module to add records. Each list screen offers view, edit, and delete where applicable.</p>
          </>
        )}
      </HelpModal>
    </div>
  )
}

export default Projects




